// Utilities for testing PSI components.

import { act, fireEvent, screen, within } from '@testing-library/react';
import { global_textinput_test_handlers, UtilityText } from 'component/text';
import { useEffect, useState } from 'react';
import { View } from 'react-native';

import type { TestState } from './datastore';
import { Datastore, useModuleObject } from './datastore';
import { assembleConfig, assembleScreenSet, moduleMapFeatureDefaults, noDefaultFeatures } from './features';
import { getStructureForKey, StackedScreen } from './instance';
import { personaA } from './testpersonas';

const undefTestState = null as TestState | null;

export type WithFeaturesProps = {
    dataRef?: any;
    siloKey?: string;
    structureKey?: string;
    instanceKey?: string;
    features?: Record<string, any>;
    language?: string;
    personaPreview?: { key: string; name: string; photoUrl: string };
    testState?: TestState;
    children: any;
};

export function WithFeatures({
    dataRef,
    siloKey = 'test',
    structureKey = 'componentdemo',
    instanceKey = 'test',
    features = {},
    language = 'english',
    personaPreview = personaA,
    testState,
    children,
}: WithFeaturesProps) {
    const activeFeatures = { ...noDefaultFeatures(structureKey), ...features };
    const storedDefaultFeatures = useModuleObject(moduleMapFeatureDefaults, structureKey);

    const structure = getStructureForKey(structureKey);
    const config = assembleConfig({ structure, activeFeatures, storedDefaultFeatures });
    return (
        <Datastore
            config={config}
            instanceKey={instanceKey}
            isLive={false}
            language={language}
            personaPreview={personaPreview}
            ref={dataRef}
            siloKey={siloKey}
            structureKey={structureKey}
            testState={testState}
        >
            {children}
        </Datastore>
    );
}

export type TestInstanceProps = {
    dataRef: any;
    structureKey: string;
    siloKey?: string;
    instanceKey?: string;
    screenKey?: string | null;
    params?: Record<string, any>;
    features?: Record<string, any>;
    testState?: TestState;
    personaPreview?: { key: string; name: string; photoUrl: string };
};

export function TestInstance({
    dataRef,
    structureKey,
    siloKey = 'test',
    instanceKey = 'test',
    screenKey = null,
    params = {},
    features = {},
    testState,
    personaPreview = personaA,
}: TestInstanceProps) {
    const structure = getStructureForKey(structureKey);
    const activeFeatures = { ...noDefaultFeatures(structureKey), ...features };
    const screenSet = assembleScreenSet({ structure, activeFeatures, storedDefaultFeatures: {} });
    return (
        <WithEnv
            dataRef={dataRef}
            features={activeFeatures}
            instanceKey={instanceKey}
            personaPreview={personaPreview}
            siloKey={siloKey}
            structureKey={structureKey}
            testState={testState}
        >
            <StackedScreen screenInstance={{ structureKey, instanceKey, screenKey, params }} screenSet={screenSet} />
        </WithEnv>
    );
}

export const WithEnv = WithFeatures;

export type TestDatastoreProps = {
    dataRef?: any;
    testState?: TestState;
    siloKey?: string;
    structureKey?: string;
    instanceKey?: string;
    language?: string;
    personaPreview?: { key: string; name: string; photoUrl: string };
    config?: Record<string, any>;
    appData?: any;
    children: React.ReactNode;
};

export function TestDatastore({
    dataRef,
    testState,
    siloKey = 'test',
    structureKey = 'test',
    instanceKey = 'test',
    language = 'english',
    personaPreview = personaA,
    config,
    appData,
    children,
}: TestDatastoreProps) {
    return (
        <Datastore
            appData={appData}
            config={config}
            instanceKey={instanceKey}
            isLive={false}
            language={language}
            personaPreview={personaPreview}
            ref={dataRef}
            siloKey={siloKey}
            structureKey={structureKey}
            testState={testState}
        >
            {children}
        </Datastore>
    );
}

export function getMatchingObject(dataRef, type, data) {
    const objects = dataRef.current._getData()[type];
    if (!objects) {
        console.error('No objects of type', type);
        return false;
    }
    for (const key in objects) {
        if (objectIsSubset(data, objects[key])) return key;
    }
    throw new Error(type + ' not found: ' + JSON.stringify(data));
}

function objectIsSubset(subset, superset) {
    for (const key in subset) {
        if (subset[key] !== superset[key]) return false;
    }
    return true;
}

async function testStoryActionAsync(parent, action) {
    if (action.action === 'click') {
        const element = await parent.findByTestId(action.matcher);
        await act(async () => {
            await fireEvent.click(element);
        });
    } else if (action.action === 'input') {
        const onChangeText = global_textinput_test_handlers[action.matcher];
        await onChangeText?.(action.text);
    } else if (action.action === 'within') {
        const withinParent = await parent.findByTestId(action.matcher);
        await testStoryActionAsync(within(withinParent), action.innerAction);
    } else if (action.action === 'popup') {
        const popupContent = await parent.findByTestId('popup-content');
        await testStoryActionAsync(within(popupContent), action.popupAction);
    } else if (action.action === 'popup-close') {
        const popupContent = await parent.findByTestId('Close Popup');
        await fireEvent.click(popupContent);
    } else if (action.action === 'check_text') {
        const textFound = parent.getByText(action.expectedText);
        if (!textFound) {
            throw new Error(`Expected text "${action.expectedText}" not found.`);
        }
    } else if (action.action === 'key_press') {
        await fireEvent.keyDown(window, {
            charCode: action.key.keyCode,
            ctrlKey: action.key.ctrlKey,
            shiftKey: action.key.shiftKey,
            altKey: action.key.altKey,
        });
    } else if (action.action === 'hover') {
        await fireEvent.mouseEnter(await parent.findByTestId(action.matcher));
    } else {
        throw new Error('Unsupported action: ' + action.action);
    }
}

export async function testStoryActionListAsync(actions) {
    for (const action of actions) {
        await act(async () => {
            await testStoryActionAsync(screen, action);
        });
    }
}
